import { lazy, useEffect, useMemo, Suspense } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { Navigate } from '~/utils/navigation';
import { TracedRoutes } from '@comeon/error-reporter';
import {
	selectFranchiseId as selectAccountFranchiseId,
	selectTestPlayer,
	selectCurrency
} from '~/components/account/ducks/index.js';
import { selectFranchiseId } from '~/components/brand/ducks/selectors.js';
import useCasinoRoutes from '../router/use-casino-routes';
import ProductRedirect from '~/components/brand/product-redirect.js';
import {
	closeLiveCasinoFeedSocket,
	establishLiveCasinoFeedSocketConnection,
	invalidateLiveCasinoData
} from '~/components/live-casino/ducks/index';
import Game from '~/components/casino-shared/game-async';
import ScrollToTop from '~/utils/scroll-to-top';
import { useDispatch, useSelector } from 'react-redux';
import { getRelativePaths } from '~/utils/pathname';
import NullLoader from '~/components/null-loader';
import ExploreSkeleton from '../casino-shared/explore/explore-skeleton';
import GamesGridSkeleton, { GamesGridSkeletonContainer } from '../casino-shared/styles/games-grid-skeleton';
import { selectAuthenticated } from '~/components/authentication/ducks/selectors';
import LiveCasinoSeoHeaderContainer from './live-casino-seo-header-container';

const Explore = lazy(
	() =>
		import(
			/* webpackChunkName: "shared-explore" */
			'~/components/casino-shared/explore/explore'
		)
);

const AllGames = lazy(
	() =>
		import(
			/* webpackChunkName: "all-games" */
			'~/components/casino-shared/all-games/all-games'
		)
);

const MyGames = lazy(
	() =>
		import(
			/* webpackChunkName: "my-games" */
			'~/components/casino-shared/my-games/my-games'
		)
);

const SubmenuGames = lazy(() => import('~/components/casino-shared/subnavigation/games'));

const ConnectedSearchResults = lazy(() => import('~/components/search/search-results-container'));

const ExclusiveGameWidgetNotifications = lazy(
	() => import('~/components/casino-shared/exclusive-game-widget/notification/notification')
);

const ExploreProduct = () => (
	<ProductRedirect product="live-casino" triggerMainContentLoading={false}>
		<Suspense fallback={<ExploreSkeleton />}>
			<Explore />
		</Suspense>
	</ProductRedirect>
);

const AllGamesProduct = () => (
	<ProductRedirect product="live-casino" triggerMainContentLoading={false}>
		<Suspense
			fallback={
				<GamesGridSkeletonContainer>
					<GamesGridSkeleton />
				</GamesGridSkeletonContainer>
			}
		>
			<AllGames />
		</Suspense>
	</ProductRedirect>
);

const MyGamesProduct = () => (
	<ProductRedirect product="live-casino">
		<MyGames />
	</ProductRedirect>
);

const SubMenuGamesProduct = () => (
	<ProductRedirect product="live-casino" triggerMainContentLoading={false}>
		<Suspense
			fallback={
				<GamesGridSkeletonContainer>
					<GamesGridSkeleton />
				</GamesGridSkeletonContainer>
			}
		>
			<SubmenuGames />
		</Suspense>
	</ProductRedirect>
);

const LiveCasinoRoutes = () => {
	const dispatch = useDispatch();
	const testPlayer = useSelector(selectTestPlayer);
	const currency = useSelector(selectCurrency);
	const accountFranchiseId = useSelector(selectAccountFranchiseId);
	const franchiseId = useSelector(selectFranchiseId);
	const location = useLocation();
	const authenticated = useSelector(selectAuthenticated);
	const {
		ROUTE_LIVE_CASINO,
		ROUTE_LIVE_CASINO_EXPLORE,
		ROUTE_LIVE_CASINO_GAME,
		ROUTE_LIVE_CASINO_SEARCH,
		ROUTE_LIVE_CASINO_SUBMENU,
		ROUTE_LIVE_CASINO_ALL_GAMES,
		ROUTE_LIVE_CASINO_MY_GAMES,
		ROUTE_LIVE_CASINO_MY_GAMES_LOVED
	} = useCasinoRoutes();

	const paths = useMemo(() => {
		const CHILD_ROUTES = [
			ROUTE_LIVE_CASINO,
			ROUTE_LIVE_CASINO_EXPLORE,
			ROUTE_LIVE_CASINO_GAME,
			ROUTE_LIVE_CASINO_SEARCH,
			ROUTE_LIVE_CASINO_SUBMENU,
			ROUTE_LIVE_CASINO_ALL_GAMES,
			ROUTE_LIVE_CASINO_MY_GAMES,
			ROUTE_LIVE_CASINO_MY_GAMES_LOVED
		];
		return getRelativePaths(CHILD_ROUTES, ROUTE_LIVE_CASINO);
	}, [
		ROUTE_LIVE_CASINO,
		ROUTE_LIVE_CASINO_ALL_GAMES,
		ROUTE_LIVE_CASINO_EXPLORE,
		ROUTE_LIVE_CASINO_GAME,
		ROUTE_LIVE_CASINO_MY_GAMES,
		ROUTE_LIVE_CASINO_MY_GAMES_LOVED,
		ROUTE_LIVE_CASINO_SEARCH,
		ROUTE_LIVE_CASINO_SUBMENU
	]);

	useEffect(() => {
		dispatch(invalidateLiveCasinoData());
	}, [dispatch, accountFranchiseId, franchiseId, currency, testPlayer]);

	useEffect(() => {
		dispatch(establishLiveCasinoFeedSocketConnection({ currency, franchiseId }));

		return () => {
			dispatch(closeLiveCasinoFeedSocket());
		};
	}, [dispatch, currency, franchiseId]);

	return (
		<>
			<ScrollToTop />
			<LiveCasinoSeoHeaderContainer />
			<Suspense fallback={<NullLoader />}>
				<TracedRoutes key="live-casino-routes">
					<Route path={paths[ROUTE_LIVE_CASINO_EXPLORE]} element={<ExploreProduct />} />
					<Route path={paths[ROUTE_LIVE_CASINO_SEARCH]} element={<ConnectedSearchResults />} />
					<Route path={paths[ROUTE_LIVE_CASINO_ALL_GAMES]} element={<AllGamesProduct />} />
					<Route path={`${paths[ROUTE_LIVE_CASINO_ALL_GAMES]}/:filter`} element={<AllGamesProduct />} />
					<Route path={`${paths[ROUTE_LIVE_CASINO_SUBMENU]}/:submenu`} element={<SubMenuGamesProduct />} />
					<Route
						path={paths[ROUTE_LIVE_CASINO_MY_GAMES]}
						element={<Navigate to={`${ROUTE_LIVE_CASINO_MY_GAMES_LOVED}`} replace />}
					/>
					<Route path={`${paths[ROUTE_LIVE_CASINO_MY_GAMES]}/:filter`} element={<MyGamesProduct />} />
					<Route path={`${paths[ROUTE_LIVE_CASINO_GAME]}/:gameCode/:gameMode`} element={<Game />} />
					<Route
						path="*"
						element={
							// TODO (navigation) - use search params or redux for state
							<Navigate
								to={`${ROUTE_LIVE_CASINO_EXPLORE}${location.search}`}
								replace
								state={location.state}
							/>
						}
					/>
				</TracedRoutes>
				{authenticated && <ExclusiveGameWidgetNotifications />}
			</Suspense>
		</>
	);
};

export default LiveCasinoRoutes;
